import { useState } from 'react';
import { useController, useFormContext } from 'react-hook-form';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faIndustryWindows,
  faFlag,
  faEllipsisVertical,
  faSquareXmark,
  faFlaskGear,
  faChevronUp,
  faChevronDown,
  faPen,
  faTrash,
} from '@fortawesome/pro-light-svg-icons';
import { faFlag as faFlagSolid } from '@fortawesome/pro-solid-svg-icons';
import { IconButton, Badge, DropdownMenu } from '@nexelem/components';
import { Collapse } from '@nexelem/deprecated-theme';
import { useDateFormatter } from '@nexelem/utils';
import { clsx } from 'clsx';
import { rootGridTemplateColumns } from '../../../../utils/gridTemplateColumns';
import { FormTextInput } from '@nexelem/form-components';
import { useCustomTranslation } from '../../../../../../../../hooks/useCustomTranslation';
import { SimulationLotMainSkeleton, SimulationLotTree } from '.';
import { useSimulationLazyLoading } from '../../../../hooks';
import { TaskView } from '@nexelem/graphne-api';
import { openChangeTechnologyModal, openRemoveTaskModal } from '../../../../modals';
import { getProductionOrderNumber } from '../../../../utils/getProductionOrderNumber';

export type Variant = 'default' | 'priority';

const borderByVariant: Record<Variant, { collapsed: string; expanded: string }> = {
  default: {
    collapsed: 'border-border-container rounded-t-xs rounded-b-xs',
    expanded: 'border-x-border-container border-t-border-container border-b-border-container rounded-t-xs',
  },
  priority: {
    collapsed: 'border-border-brand-lighter rounded-t-xs rounded-b-xs',
    expanded: 'border-x-border-brand-lighter border-t-border-brand-lighter border-b-border-container rounded-t-xs',
  },
};

type SimulationLotMainItemProps = {
  task: TaskView;
  tasks: TaskView[];
  index: number;
};

const SimulationLotMainItem = ({ task, tasks, index }: SimulationLotMainItemProps) => {
  const { tWrapper } = useCustomTranslation('products.simulationPlan');
  const [isExpanded, setIsExpanded] = useState(false);
  const formatDate = useDateFormatter({ displayWithHours: true });
  const { setValue } = useFormContext();

  const {
    field: amountField,
    fieldState: { isDirty: amountIsDirty },
  } = useController({
    name: `${task.id}.amount`,
  });

  const {
    field: priorityField,
    fieldState: { isDirty: priorityIsDirty },
  } = useController({
    name: `${task.id}.priority`,
  });

  const {
    field: deleteField,
    fieldState: { isDirty: deleteIsDirty },
  } = useController({
    name: `${task.id}.delete`,
  });

  const {
    field: technologyField,
    fieldState: { isDirty: technologyIsDirty },
  } = useController({
    name: `${task.id}.technology`,
  });

  const isDeleted = deleteIsDirty && deleteField.value === true;
  const isEdited = (amountIsDirty || priorityIsDirty || technologyIsDirty) && !isDeleted;

  const variant: Variant = priorityField.value ? 'priority' : 'default';
  const borderClass = isExpanded ? borderByVariant[variant].expanded : borderByVariant[variant].collapsed;

  const productCode = task.productDetails?.code?.split(' ')?.[0] ?? '-';
  const orderNumber = getProductionOrderNumber(task) || '-';

  const handlePriorityClick = () => {
    const newPriority = !priorityField.value;
    const currentOrderNumber = getProductionOrderNumber(task);

    if (!currentOrderNumber) {
      priorityField.onChange(newPriority);
      return;
    }

    tasks.forEach((t) => {
      if (getProductionOrderNumber(t) === currentOrderNumber) {
        setValue(`${t.id}.priority`, newPriority, { shouldDirty: true });
      }
    });
  };

  return (
    <>
      <div
        style={rootGridTemplateColumns}
        className={clsx(
          'grid items-center gap-2 border p-2',
          isDeleted ? 'bg-surface-disabled' : 'bg-surface-secondary',
          borderClass,
        )}
      >
        {/* Grid Column 1 */}
        <div className="flex items-center gap-2">
          <IconButton
            className="h-8.5 w-8.5"
            disabled={isDeleted}
            Icon={() => (
              <FontAwesomeIcon
                className={clsx(priorityField.value ? 'text-icon-brand' : 'text-icon-primary')}
                icon={priorityField.value ? faFlagSolid : faFlag}
              />
            )}
            onClick={handlePriorityClick}
          />
          <FontAwesomeIcon
            className="min-h-5 w-5 rounded-full border border-border-container bg-surface-container-lighter p-1"
            icon={faIndustryWindows}
          />
        </div>

        {/* Grid Column 2 */}
        <div className="flex flex-col gap-0.5">
          <p className="inline-flex gap-2 text-s11-sb text-text-secondary-darker uppercase">
            <span>{tWrapper('taskItemProductNumber')}</span>
            <span>{productCode}</span>
          </p>
          <p className="text-s16-sb">{task.productDetails.name}</p>
          {isEdited && (
            <Badge className="w-fit" StartIcon={<FontAwesomeIcon icon={faPen} />} variant="highlighted">
              {tWrapper('taskItemEdited')}
            </Badge>
          )}
          {isDeleted && (
            <Badge className="w-fit" StartIcon={<FontAwesomeIcon icon={faTrash} />} variant="default">
              {tWrapper('taskItemDeleted')}
            </Badge>
          )}
          <p className="inline-flex gap-2 text-s11-sb uppercase">
            <span className="text-text-status-green">
              {tWrapper('taskItemBatchNumber')} {index + 1}
            </span>
            <span className="rounded-xs border border-border-primary px-1 text-text-secondary-darker">
              {tWrapper('taskItemOrderNumber')} {orderNumber}
            </span>
          </p>
        </div>

        {/* Grid Column 3 */}
        <div className="flex items-center justify-end">
          <FormTextInput
            className="w-full"
            disabled={isDeleted}
            inputClassName="text-right"
            name={amountField.name}
            type="number"
          />
        </div>

        {/* Grid Column 4 */}
        <div className="text-s18 text-text-secondary-darker">{task.progress.totalAmount.unit.name}</div>

        {/* Grid Column 5 */}
        <div className="flex flex-col gap-0.5">
          <p className="inline-flex gap-2 text-s11-sb text-text-secondary-darker uppercase">
            <span>{tWrapper('taskItemArea')}</span>
            <span>{task.queue?.area?.name || '-'}</span>
          </p>
          <span className="text-s16">{task.queue?.shortName || '-'}</span>
        </div>

        {/* Grid Column 6 */}
        <div>
          <span className="text-s16">{technologyField.value || task.qapsSimulationData?.recipeVersion || '-'}</span>
        </div>

        {/* Grid Column 7 */}
        <div></div>

        {/* Grid Column 8 */}
        <div className="text-s18">{formatDate(task.start)}</div>

        {/* Grid Column 9 */}
        <div className="grid grid-cols-2 gap-1">
          {!isDeleted ? (
            <DropdownMenu.Root>
              <DropdownMenu.Trigger>
                <IconButton Icon={() => <FontAwesomeIcon icon={faEllipsisVertical} />} size="small" />
              </DropdownMenu.Trigger>
              <DropdownMenu.Content align="end">
                {!task.originalTaskId && (
                  <DropdownMenu.Button
                    StartIcon={<FontAwesomeIcon icon={faFlaskGear} />}
                    onClick={() =>
                      openChangeTechnologyModal({
                        task,
                        currentTechnology: technologyField.value,
                        onChange: technologyField.onChange,
                      })
                    }
                  >
                    {tWrapper('taskItemChangeTechnology')}
                  </DropdownMenu.Button>
                )}
                <DropdownMenu.Button
                  StartIcon={<FontAwesomeIcon icon={faSquareXmark} />}
                  onClick={() =>
                    openRemoveTaskModal({
                      task,
                      onRemove: () => deleteField.onChange(!deleteField.value),
                    })
                  }
                >
                  {tWrapper('simulationRemove')}
                </DropdownMenu.Button>
              </DropdownMenu.Content>
            </DropdownMenu.Root>
          ) : (
            <div></div>
          )}

          {Boolean(task.dependsOn?.length) && (
            <IconButton
              disabled={isDeleted}
              Icon={() => <FontAwesomeIcon icon={isExpanded ? faChevronUp : faChevronDown} />}
              size="small"
              onClick={() => setIsExpanded(!isExpanded)}
            />
          )}
        </div>
      </div>

      {Boolean(task.dependsOn?.length) && (
        <Collapse in={isExpanded}>
          <div className="bg-surface-secondary">
            {isExpanded && <SimulationLotTree index={index} key={task.id} taskId={task.id} variant={variant} />}
          </div>
        </Collapse>
      )}
    </>
  );
};

type SimulationLotMainProps = {
  tasks: TaskView[];
  isLoading: boolean;
  hasNextPage: boolean;
  fetchNextPage: () => void;
};

export const SimulationLotMain = ({ tasks, isLoading, hasNextPage, fetchNextPage }: SimulationLotMainProps) => {
  const { lastItemRef, shouldShowSkeletons } = useSimulationLazyLoading({
    hasNextPage,
    isLoading,
    fetchNextPage,
  });

  if (isLoading || tasks.length === 0) {
    return <SimulationLotMainSkeleton count={10} />;
  }

  return (
    <div className="flex min-w-405 flex-col gap-3">
      {tasks.map((task, index) => {
        const isLastItem = index === tasks.length - 1;

        return (
          <div key={task.id} ref={isLastItem ? lastItemRef : undefined}>
            <SimulationLotMainItem index={index} task={task} tasks={tasks} />
          </div>
        );
      })}

      {shouldShowSkeletons && <SimulationLotMainSkeleton />}
    </div>
  );
};
